/**
 * useShellLibrary - the character library and the shared loadouts, as the shell uses them.
 *
 * - `activeLibrary`: the session's shared library (there is no play outside a
 *   session; the browser's own library retired in assets stage 4).
 * - `handleSaveCombatantToLibrary(id)`: the other direction of the same
 *   door - save the fight's version of somebody back up. Offers a choice,
 *   because a template with 4/20 wounds baked in is rarely what you want
 *   next session.
 * - LOADOUTS (D4) are TEMPLATES: applying one stamps a copy of its weapons
 *   onto the actor's own sheet, so two marines with "the same" bolter still
 *   track their own ammo.
 *
 * Extracted from DeathWatchSimulator; the log lines are unchanged.
 */
import { useCallback, useMemo } from 'react';
import * as multiplayer from '../multiplayer';
import { withStanding, standingEntries } from '../engine/roster/standing';
import { readyForSheetEdit } from '../engine/roster/sheetEdit';
import useSessionCharacterLibrary from './useSessionCharacterLibrary';
import useLoadoutLibrary from './useLoadoutLibrary';
import { infoLine } from './shellCombatant';
import { loadoutFromWeapons } from '../engine/roster/factories';

const SAVE_CHOICES = [
  { label: 'Sheet only (a clean template)', value: 'sheet' },
  { label: 'As they stand (wounds, conditions, ammunition, pins)', value: 'state' },
];

/** The library this client reads and writes: the session's. */
function useActiveLibrary(sync) {
  return useSessionCharacterLibrary({
    sessionCharacters: sync.sessionCharacters,
    synced: sync.charactersSynced,
    pushCharacter: sync.pushCharacter,
    pushAllCharacters: sync.pushAllCharacters,
    deleteSessionCharacter: sync.deleteSessionCharacter,
  });
}

/** Save somebody back to the library, clean or wounds and all. */
function useSaveToLibrary({ roster, showDialogAsync, activeTab, addNarrative, tableAssets }, activeLibrary) {
  return useCallback(
    async (combatantId) => {
      const live = roster.find((x) => x.id === combatantId);
      if (!live) return;
      // The sheet as the Sheet tab SHOWS it: a statblock's pins folded in, or
      // the saved entry would disagree with what was on screen.
      const c = readyForSheetEdit(live);
      const choice = await showDialogAsync('choice', `Save "${c.name}" to the character library:`, {
        choices: SAVE_CHOICES,
      });
      if (!choice) return;
      const withState = choice === 'state';
      const entry = withState ? withStanding(c) : c.sheet;
      activeLibrary.saveCharacter(entry);
      // A GM with an asset library keeps a version there too (plan assets-library, stage 2); it says how it went.
      void tableAssets?.versionCharacter(entry);
      addNarrative(
        activeTab,
        infoLine(`Saved "${c.name}" to the library (${withState ? 'as they stand' : 'sheet only'}).`),
      );
    },
    [roster, showDialogAsync, activeLibrary, activeTab, addNarrative, tableAssets],
  );
}

/**
 * Save several combatants to the library as they stand, without asking
 * about each: the end-of-session snapshot (v8.414.0). One library write
 * per body, as every save is (see `standingEntries` for bodies that share
 * a sheet). The caller holds the lock; this is the verb.
 */
function useSaveStanding({ roster, activeTab, addNarrative }, activeLibrary) {
  return useCallback(
    (ids) => {
      // As the Sheet tab shows them: a statblock's pins folded into its sheet.
      const saved = roster.filter((c) => ids.includes(c.id)).map(readyForSheetEdit);
      for (const entry of standingEntries(saved)) activeLibrary.saveCharacter(entry);
      if (saved.length) {
        addNarrative(
          activeTab,
          infoLine(`Saved ${saved.length} to the library as they stand: ${saved.map((c) => c.name).join(', ')}.`),
        );
      }
    },
    [roster, activeLibrary, activeTab, addNarrative],
  );
}

/**
 * The loadout library, and the actor's two verbs on it. Its own hook, called
 * where the shell always called it, so its listener subscribes in the same order.
 *
 * @param {object} args - sessionId, playerId, actor, updateCombatantSheet, updateCombatantState, and the GM's
 *   `armouryShelves` (null without an asset library): a kit may be saved there too (plan armoury, part 6).
 */
export function useShellLoadouts(args) {
  const { sessionId, playerId, actor, updateCombatantSheet, updateCombatantState, armouryShelves } = args;
  const { loadouts, saveLoadout, deleteLoadout, applyLoadout } = useLoadoutLibrary({
    sessionId,
    playerId,
    mp: multiplayer,
  });
  const handleSaveLoadout = useCallback(
    (name) => {
      if (actor) saveLoadout(name, actor.sheet.weapons || []);
    },
    [actor, saveLoadout],
  );
  const handleApplyLoadout = useCallback(
    (loadout) => {
      if (!actor) return;
      const armed = applyLoadout(actor, loadout);
      updateCombatantSheet(actor.id, () => armed.sheet);
      updateCombatantState(actor.id, (st) => ({ ...st, weaponAmmo: armed.state.weaponAmmo }));
    },
    [actor, applyLoadout, updateCombatantSheet, updateCombatantState],
  );
  // The actor's weapons as a kit in the GM's library; null without one, so the menu offers no such button.
  const saveKitToLibrary = armouryShelves?.saveLoadout;
  const handleSaveLoadoutToLibrary = useMemo(
    () =>
      saveKitToLibrary
        ? (name) => {
            if (actor) saveKitToLibrary(loadoutFromWeapons(name, actor.sheet.weapons || []));
          }
        : null,
    [actor, saveKitToLibrary],
  );
  return { loadouts, saveLoadout, deleteLoadout, handleSaveLoadout, handleApplyLoadout, handleSaveLoadoutToLibrary };
}

/**
 * @param {object} args - sessionId, sync, roster, activeTab, showDialogAsync, addNarrative, and the GM's
 *   `tableAssets` (null without an asset library).
 * @returns {{ activeLibrary, handleSaveCombatantToLibrary, handleSaveStanding }}
 */
export default function useShellLibrary(args) {
  const activeLibrary = useActiveLibrary(args.sync);
  const handleSaveCombatantToLibrary = useSaveToLibrary(args, activeLibrary);
  const handleSaveStanding = useSaveStanding(args, activeLibrary);
  return { activeLibrary, handleSaveCombatantToLibrary, handleSaveStanding };
}
